<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, nextTick, onMounted, reactive, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import TalkviaForumAPI from 'dashboard/api/talkviaForum';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Select from 'dashboard/components-next/combobox/ComboBox.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ForumComposerExtras from './ForumComposerExtras.vue';
import ForumAuthor from './ForumAuthor.vue';
import ForumCategoryBadge from './ForumCategoryBadge.vue';

const route = useRoute();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const loading = ref(true);
const savingReply = ref(false);
const interactionId = ref(null);
const topic = ref(null);
const replyBody = ref('');
const replyFiles = ref([]);
const replyMentionedUsers = ref([]);
const replySort = ref('relevant');
const replyComposer = ref(null);
const replyComposerCollapsed = ref(true);
const replyTo = ref(null);
const replyPreview = ref(false);
const highlightedPost = ref(null);
const reportTarget = ref(null);
const reportForm = reactive({ reason: 'spam', details: '' });
const reportReasons = [
  { value: 'spam', label: 'Spam ou propaganda' },
  { value: 'abuse', label: 'Conteúdo abusivo' },
  { value: 'harassment', label: 'Assédio' },
  { value: 'misinformation', label: 'Informação enganosa' },
  { value: 'privacy', label: 'Exposição de dados pessoais' },
  { value: 'other', label: 'Outro motivo' },
];

const canParticipate = computed(
  () => topic.value?.membership?.can_participate !== false
);
const replySortOptions = [
  { value: 'relevant', label: 'Mais relevantes' },
  { value: 'recent', label: 'Mais recentes' },
  { value: 'oldest', label: 'Mais antigas' },
];
const sortedPosts = computed(() => {
  const posts = [...(topic.value?.posts || [])];
  return posts.sort((first, second) => {
    if (first.id === topic.value.accepted_post_id) return -1;
    if (second.id === topic.value.accepted_post_id) return 1;
    if (replySort.value === 'relevant') {
      return (
        second.likes_count - first.likes_count ||
        new Date(first.created_at) - new Date(second.created_at)
      );
    }
    const direction = replySort.value === 'recent' ? -1 : 1;
    return (
      direction * (new Date(first.created_at) - new Date(second.created_at))
    );
  });
});
const participants = computed(() => {
  const users = [
    topic.value?.author,
    ...(topic.value?.posts || []).map(post => post.author),
  ].filter(Boolean);
  return [...new Map(users.map(user => [user.id, user])).values()];
});
const goToSolution = () =>
  document
    .getElementById(`forum-post-${topic.value.accepted_post_id}`)
    ?.scrollIntoView({ behavior: 'smooth', block: 'center' });

const loadTopic = async () => {
  loading.value = true;
  try {
    const { data } = await TalkviaForumAPI.topic(route.params.topicId);
    topic.value = data;
  } catch (error) {
    useAlert('O tópico não foi encontrado ou não está mais disponível.');
    router.push(accountScopedRoute('talkvia_forum'));
  } finally {
    loading.value = false;
  }
};

const createReply = async () => {
  if (
    savingReply.value ||
    !canParticipate.value ||
    replyBody.value.trim().length < 2
  )
    return;
  savingReply.value = true;
  try {
    const { data } = await TalkviaForumAPI.createPost(
      topic.value.id,
      replyBody.value,
      replyFiles.value,
      replyMentionedUsers.value.map(user => user.id),
      replyTo.value?.id || null
    );
    topic.value.posts.push(data);
    topic.value.posts_count += 1;
    replyBody.value = '';
    replyTo.value = null;
    replyPreview.value = false;
    replyFiles.value = [];
    replyMentionedUsers.value = [];
    replyComposerCollapsed.value = true;
    useAlert('Resposta publicada.');
  } catch (error) {
    useAlert(
      error.response?.data?.error || 'Não foi possível publicar a resposta.'
    );
  } finally {
    savingReply.value = false;
  }
};

const toggleLike = async post => {
  interactionId.value = `like-${post.id}`;
  try {
    const { data } = await TalkviaForumAPI.setLike(post.id, post.liked);
    Object.assign(post, data);
  } catch (error) {
    useAlert('Não foi possível atualizar a curtida.');
  } finally {
    interactionId.value = null;
  }
};

const toggleSubscription = async () => {
  interactionId.value = 'subscription';
  try {
    await TalkviaForumAPI.setSubscription(
      topic.value.id,
      topic.value.subscribed
    );
    topic.value.subscribed = !topic.value.subscribed;
    useAlert(
      topic.value.subscribed
        ? 'Você está acompanhando este tópico.'
        : 'Acompanhamento removido.'
    );
  } catch (error) {
    useAlert('Não foi possível atualizar o acompanhamento.');
  } finally {
    interactionId.value = null;
  }
};

const acceptSolution = async post => {
  interactionId.value = `solution-${post.id}`;
  try {
    const { data } = await TalkviaForumAPI.acceptSolution(
      topic.value.id,
      post.id
    );
    topic.value = data;
    useAlert('Resposta marcada como solução.');
  } catch (error) {
    useAlert('Não foi possível marcar esta resposta como solução.');
  } finally {
    interactionId.value = null;
  }
};

const openReport = (type, record) => {
  reportTarget.value = { type, record };
  reportForm.reason = 'spam';
  reportForm.details = '';
};

const submitReport = async () => {
  interactionId.value = 'report';
  try {
    await TalkviaForumAPI.report({
      reportable_type: reportTarget.value.type,
      reportable_id: reportTarget.value.record.id,
      ...reportForm,
    });
    reportTarget.value = null;
    useAlert('Denúncia enviada para a equipe de moderação.');
  } catch (error) {
    useAlert(
      error.response?.status === 422
        ? 'Você já denunciou este conteúdo.'
        : 'Não foi possível enviar a denúncia.'
    );
  } finally {
    interactionId.value = null;
  }
};

const formatDate = value =>
  new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
const expandReplyComposer = async () => {
  replyComposerCollapsed.value = false;
  await nextTick();
  replyComposer.value
    ?.querySelector('textarea')
    ?.focus({ preventScroll: true });
  replyComposer.value?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
};

const quoteReply = async post => {
  if (
    !canParticipate.value ||
    topic.value.status !== 'open' ||
    savingReply.value
  )
    return;
  replyTo.value = post;
  replyPreview.value = false;
  await expandReplyComposer();
};
const scrollToPost = id => {
  const target = document.getElementById(`forum-post-${id}`);
  highlightedPost.value = id;
  target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  target?.focus({ preventScroll: true });
};
const copyReplyLink = async post => {
  try {
    const url = new URL(window.location.href);
    url.hash = `forum-post-${post.id}`;
    await navigator.clipboard.writeText(url.toString());
    useAlert('Link da resposta copiado.');
  } catch {
    useAlert('Não foi possível copiar o link da resposta. Tente novamente.');
  }
};
onMounted(async () => {
  await loadTopic();
  await nextTick();
  const match = window.location.hash.match(/^#forum-post-(\d+)$/);
  if (match) scrollToPost(Number(match[1]));
});
</script>

<template>
  <main
    class="flex h-full w-full min-w-0 flex-col gap-5 overflow-y-auto p-4 sm:p-6"
  >
    <div class="mx-auto w-full max-w-7xl">
      <Button
        label="Voltar à comunidade"
        icon="i-lucide-arrow-left"
        color="slate"
        variant="link"
        class="self-start"
        @click="router.push(accountScopedRoute('talkvia_forum'))"
      />
    </div>
    <div
      v-if="loading"
      class="mx-auto flex min-h-64 w-full max-w-7xl items-center justify-center"
    >
      <Icon icon="i-lucide-loader-2" class="size-7 animate-spin text-n-brand" />
    </div>
    <div
      v-else-if="topic"
      class="mx-auto grid w-full max-w-7xl items-start gap-5 xl:grid-cols-[minmax(0,1fr)_17rem]"
    >
      <div class="min-w-0 space-y-5">
        <article
          class="grid overflow-hidden rounded-xl border border-n-weak bg-n-solid-1 shadow-sm sm:grid-cols-[9rem_minmax(0,1fr)]"
        >
          <ForumAuthor :author="topic.author" />
          <div class="min-w-0 p-5 sm:p-6">
            <div class="flex flex-wrap items-center gap-2">
              <span
                v-if="topic.pinned"
                class="rounded-full bg-n-violet-3 px-2 py-1 text-xs font-medium text-n-violet-11"
                >Fixado</span
              ><span
                v-if="topic.solved"
                class="rounded-full bg-n-teal-3 px-2 py-1 text-xs font-medium text-n-teal-11"
                >Resolvido</span
              >
              <ForumCategoryBadge :category="topic.category" />
            </div>
            <h1
              class="mt-4 break-words text-2xl font-semibold leading-tight text-n-slate-12 sm:text-3xl"
            >
              {{ topic.title }}
            </h1>
            <p class="mt-3 text-xs text-n-slate-11">
              {{ formatDate(topic.created_at) }}
            </p>
            <p
              class="mt-6 whitespace-pre-wrap break-words text-sm leading-7 text-n-slate-11"
            >
              {{ topic.body }}
            </p>
            <div
              v-if="topic.mentions?.length"
              class="mt-4 flex flex-wrap gap-1.5"
            >
              <span
                v-for="user in topic.mentions"
                :key="user.id"
                class="rounded-full bg-n-violet-3 px-2.5 py-1 text-xs font-medium text-n-violet-11"
              >
                @{{ user.name }}
              </span>
            </div>
            <div
              v-if="topic.attachments?.length"
              class="mt-4 grid gap-2 sm:grid-cols-2"
            >
              <a
                v-for="attachment in topic.attachments"
                :key="attachment.id"
                :href="attachment.url"
                class="group flex min-w-0 items-center gap-3 rounded-xl border border-n-weak bg-n-alpha-1 p-3 hover:bg-n-alpha-2"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  v-if="attachment.content_type.startsWith('image/')"
                  :src="attachment.preview_url"
                  :alt="attachment.filename"
                  class="size-12 rounded-lg object-cover"
                />
                <span
                  v-else
                  class="grid size-12 shrink-0 place-items-center rounded-lg bg-n-violet-3 text-n-violet-11"
                >
                  <Icon icon="i-lucide-file-text" class="size-5" />
                </span>
                <span class="min-w-0">
                  <strong class="block truncate text-sm text-n-slate-12">{{
                    attachment.filename
                  }}</strong>
                  <span class="text-xs text-n-slate-9">Abrir anexo</span>
                </span>
                <Icon
                  icon="i-lucide-download"
                  class="ml-auto size-4 shrink-0 text-n-slate-9 group-hover:text-n-brand"
                />
              </a>
            </div>
            <div
              class="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-n-weak pt-4"
            >
              <Button
                v-if="topic.status === 'open' && canParticipate"
                label="Responder"
                icon="i-lucide-reply"
                sm
                @click="expandReplyComposer"
              />
              <div class="flex gap-2">
                <Button
                  :label="topic.subscribed ? 'Acompanhando' : 'Acompanhar'"
                  :icon="
                    topic.subscribed ? 'i-lucide-bell-ring' : 'i-lucide-bell'
                  "
                  color="slate"
                  variant="faded"
                  size="sm"
                  :is-loading="interactionId === 'subscription'"
                  @click="toggleSubscription"
                /><Button
                  label="Denunciar"
                  icon="i-lucide-flag"
                  color="slate"
                  variant="ghost"
                  size="sm"
                  @click="openReport('topic', topic)"
                />
              </div>
            </div>
          </div>
        </article>

        <section id="forum-replies">
          <div class="mb-3 flex items-center justify-between gap-3">
            <h2 class="text-base font-semibold text-n-slate-12">
              {{ topic.posts_count }}
              {{ topic.posts_count === 1 ? 'resposta' : 'respostas' }}
            </h2>
            <Select
              v-if="topic.posts.length > 1"
              v-model="replySort"
              :options="replySortOptions"
              class="w-44"
            />
          </div>
          <div
            v-if="!topic.posts.length"
            class="rounded-xl border border-dashed border-n-weak p-10 text-center text-sm text-n-slate-9"
          >
            Seja a primeira pessoa a responder.
          </div>
          <div v-else class="space-y-3">
            <article
              v-for="post in sortedPosts"
              :id="`forum-post-${post.id}`"
              :key="post.id"
              tabindex="-1"
              class="grid scroll-mt-4 overflow-hidden rounded-xl border bg-n-solid-1 sm:grid-cols-[9rem_minmax(0,1fr)]"
              :class="[
                topic.accepted_post_id === post.id
                  ? 'border-n-teal-7 ring-1 ring-n-teal-5'
                  : 'border-n-weak',
                highlightedPost === post.id && 'ring-2 ring-n-brand',
              ]"
            >
              <ForumAuthor :author="post.author" />
              <div class="min-w-0 p-4 sm:p-5">
                <div class="flex flex-wrap items-start justify-between gap-3">
                  <p class="text-xs text-n-slate-11">
                    {{ formatDate(post.created_at)
                    }}<span v-if="post.edited_at"> · editado</span>
                  </p>
                  <span
                    v-if="topic.accepted_post_id === post.id"
                    class="inline-flex items-center gap-1 rounded-full bg-n-teal-3 px-2 py-1 text-xs font-medium text-n-teal-11"
                    ><Icon
                      icon="i-lucide-circle-check"
                      class="size-3.5"
                    />Solução aceita</span
                  >
                </div>
                <div
                  v-if="post.reply_to"
                  class="mt-4 rounded-lg border-l-2 border-n-brand bg-n-alpha-1 p-3"
                >
                  <p
                    v-if="post.reply_to.unavailable"
                    class="text-xs text-n-slate-11"
                  >
                    Resposta original indisponível.
                  </p>
                  <button
                    v-else
                    type="button"
                    class="w-full text-left"
                    @click="scrollToPost(post.reply_to.id)"
                  >
                    <span class="text-xs font-medium text-n-brand"
                      >Em resposta a {{ post.reply_to.author.name }} · #{{
                        post.reply_to.id
                      }}</span
                    >
                    <span
                      class="mt-1 block line-clamp-3 whitespace-pre-wrap break-words text-xs text-n-slate-11"
                      >{{ post.reply_to.excerpt }}</span
                    >
                  </button>
                </div>
                <p
                  class="mt-4 whitespace-pre-wrap break-words text-sm leading-7 text-n-slate-11"
                >
                  {{ post.body }}
                </p>
                <div
                  v-if="post.mentions?.length"
                  class="mt-3 flex flex-wrap gap-1.5"
                >
                  <span
                    v-for="user in post.mentions"
                    :key="user.id"
                    class="rounded-full bg-n-violet-3 px-2.5 py-1 text-xs font-medium text-n-violet-11"
                  >
                    @{{ user.name }}
                  </span>
                </div>
                <div
                  v-if="post.attachments?.length"
                  class="mt-3 grid gap-2 sm:grid-cols-2"
                >
                  <a
                    v-for="attachment in post.attachments"
                    :key="attachment.id"
                    :href="attachment.url"
                    class="group flex min-w-0 items-center gap-3 rounded-lg border border-n-weak bg-n-alpha-1 p-2.5 hover:bg-n-alpha-2"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Icon
                      icon="i-lucide-paperclip"
                      class="size-4 shrink-0 text-n-slate-9"
                    />
                    <span
                      class="min-w-0 flex-1 truncate text-xs font-medium text-n-slate-11"
                      >{{ attachment.filename }}</span
                    >
                    <Icon
                      icon="i-lucide-download"
                      class="size-4 shrink-0 text-n-slate-9"
                    />
                  </a>
                </div>
                <div
                  class="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-n-weak pt-3"
                >
                  <Button
                    :label="`${post.liked ? 'Curtido' : 'Curtir'} · ${post.likes_count}`"
                    icon="i-lucide-thumbs-up"
                    :color="post.liked ? 'blue' : 'slate'"
                    :variant="post.liked ? 'solid' : 'ghost'"
                    size="xs"
                    :is-loading="interactionId === `like-${post.id}`"
                    @click="toggleLike(post)"
                  />
                  <div class="flex flex-wrap gap-1">
                    <Button
                      v-if="topic.status === 'open' && canParticipate"
                      label="Responder e citar"
                      icon="i-lucide-quote"
                      color="slate"
                      variant="ghost"
                      size="xs"
                      :disabled="savingReply"
                      @click="quoteReply(post)"
                    />
                    <Button
                      label="Copiar link"
                      icon="i-lucide-link"
                      color="slate"
                      variant="ghost"
                      size="xs"
                      @click="copyReplyLink(post)"
                    />
                    <Button
                      v-if="
                        topic.can_edit && topic.accepted_post_id !== post.id
                      "
                      label="Marcar como solução"
                      icon="i-lucide-circle-check"
                      color="teal"
                      variant="ghost"
                      size="xs"
                      :is-loading="interactionId === `solution-${post.id}`"
                      @click="acceptSolution(post)"
                    /><Button
                      label="Denunciar"
                      icon="i-lucide-flag"
                      color="slate"
                      variant="ghost"
                      size="xs"
                      @click="openReport('post', post)"
                    />
                  </div>
                </div>
              </div>
            </article>
          </div>
        </section>

        <form
          v-if="topic.status === 'open' && !replyComposerCollapsed"
          ref="replyComposer"
          class="rounded-2xl border border-n-weak bg-n-solid-1 p-5 shadow-sm"
          @submit.prevent="createReply"
        >
          <div class="flex items-center gap-3">
            <span
              class="flex size-9 items-center justify-center rounded-lg bg-n-brand/10 text-n-brand"
              ><Icon
                icon="i-lucide-message-square-reply"
                class="size-5"
                aria-hidden="true"
            /></span>
            <h2 class="text-base font-semibold text-n-slate-12">
              Sua contribuição
            </h2>
            <Button
              label="Recolher"
              icon="i-lucide-chevron-down"
              color="slate"
              variant="ghost"
              size="sm"
              class="ml-auto"
              :disabled="savingReply"
              @click="replyComposerCollapsed = true"
            />
          </div>
          <p class="mt-1 text-xs text-n-slate-9">
            Compartilhe uma solução clara e evite publicar dados pessoais ou
            credenciais.
          </p>
          <div
            v-if="replyTo"
            class="mt-4 flex items-start gap-2 rounded-lg border-l-2 border-n-brand bg-n-alpha-1 p-3"
          >
            <div class="min-w-0 flex-1">
              <p class="text-xs font-medium text-n-brand">
                Respondendo a {{ replyTo.author.name }} · #{{ replyTo.id }}
              </p>
              <p class="mt-1 line-clamp-3 break-words text-xs text-n-slate-11">
                {{ replyTo.body }}
              </p>
            </div>
            <Button
              label="Remover citação"
              icon="i-lucide-x"
              color="slate"
              variant="ghost"
              size="xs"
              :disabled="savingReply"
              @click="replyTo = null"
            />
          </div>
          <div class="mt-4 flex gap-2">
            <Button
              label="Escrever resposta"
              color="slate"
              :variant="replyPreview ? 'ghost' : 'faded'"
              size="sm"
              @click="replyPreview = false"
            />
            <Button
              label="Prévia da resposta"
              color="slate"
              :variant="replyPreview ? 'faded' : 'ghost'"
              size="sm"
              @click="replyPreview = true"
            />
          </div>
          <p
            v-if="replyPreview"
            class="mt-4 min-h-32 whitespace-pre-wrap break-words rounded-lg border border-n-weak p-4 text-sm leading-7 text-n-slate-11"
          >
            {{ replyBody || 'Escreva sua resposta para visualizar a prévia.' }}
          </p>
          <TextArea
            v-else
            v-model="replyBody"
            class="mt-4"
            placeholder="Escreva sua resposta..."
            min-height="8rem"
            :max-length="20000"
            show-character-count
            :disabled="!canParticipate || savingReply"
          />
          <ForumComposerExtras
            v-model:files="replyFiles"
            v-model:mentioned-users="replyMentionedUsers"
            class="mt-3"
            :disabled="!canParticipate || savingReply"
          />
          <div class="mt-3 flex justify-end">
            <Button
              type="submit"
              label="Publicar resposta"
              icon="i-lucide-send"
              :is-loading="savingReply"
              :disabled="
                savingReply || !canParticipate || replyBody.trim().length < 2
              "
            />
          </div>
        </form>
        <button
          v-else-if="topic.status === 'open'"
          type="button"
          class="sticky bottom-4 z-20 flex w-full items-center justify-between gap-3 rounded-xl border border-n-weak bg-n-solid-2 px-4 py-3 text-left shadow-lg transition hover:border-n-brand"
          @click="expandReplyComposer"
        >
          <span class="inline-flex items-center gap-3">
            <span
              class="grid size-9 place-items-center rounded-lg bg-n-brand/10 text-n-brand"
            >
              <Icon icon="i-lucide-message-square-reply" class="size-4" />
            </span>
            <span>
              <strong class="block text-sm text-n-slate-12">
                Adicionar resposta
              </strong>
              <span class="block text-xs text-n-slate-9">
                {{
                  replyBody || replyTo || replyFiles.length
                    ? 'Continuar seu rascunho'
                    : 'Clique para abrir o editor'
                }}
              </span>
            </span>
          </span>
          <Icon icon="i-lucide-chevron-up" class="size-4 text-n-slate-9" />
        </button>
        <div
          v-else
          class="rounded-xl border border-n-amber-6 bg-n-amber-2 p-4 text-sm text-n-amber-11"
        >
          Este tópico está fechado para novas respostas.
        </div>
      </div>
      <aside class="space-y-4 xl:sticky xl:top-0">
        <section class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <h2 class="text-sm font-semibold">Sobre este tópico</h2>
          <div
            class="mt-4 flex items-center gap-2 text-sm"
            :class="topic.solved ? 'text-n-teal-11' : 'text-n-slate-11'"
          >
            <Icon
              :icon="
                topic.solved
                  ? 'i-lucide-circle-check'
                  : 'i-lucide-messages-square'
              "
              class="size-4"
            />
            {{
              topic.solved
                ? 'Solução encontrada'
                : topic.status === 'open'
                  ? 'Discussão aberta'
                  : 'Discussão encerrada'
            }}
          </div>
          <dl class="mt-4 space-y-3 text-xs">
            <div class="flex justify-between gap-3">
              <dt class="text-n-slate-11">Visualizações</dt>
              <dd class="font-medium tabular-nums">{{ topic.views_count }}</dd>
            </div>
            <div class="flex justify-between gap-3">
              <dt class="text-n-slate-11">Respostas</dt>
              <dd class="font-medium tabular-nums">{{ topic.posts_count }}</dd>
            </div>
            <div class="flex justify-between gap-3">
              <dt class="text-n-slate-11">Participantes</dt>
              <dd class="font-medium tabular-nums">
                {{ participants.length }}
              </dd>
            </div>
          </dl>
          <Button
            v-if="topic.accepted_post_id"
            label="Ver solução aceita"
            icon="i-lucide-circle-check"
            color="teal"
            variant="faded"
            class="mt-4 w-full"
            sm
            @click="goToSolution"
          />
        </section>
        <section class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <h2 class="text-sm font-semibold">Quem participa</h2>
          <div class="mt-3 space-y-3">
            <div
              v-for="person in participants.slice(0, 6)"
              :key="person.id"
              class="flex min-w-0 items-center gap-2"
            >
              <img
                v-if="person.avatar_url"
                :src="person.avatar_url"
                :alt="person.name"
                class="size-7 rounded-full object-cover"
              />
              <span
                v-else
                class="flex size-7 shrink-0 items-center justify-center rounded-full bg-n-alpha-2 text-xs text-n-slate-11"
                >{{ person.name.charAt(0) }}</span
              >
              <span class="truncate text-xs text-n-slate-11">{{
                person.name
              }}</span>
            </div>
            <p v-if="participants.length > 6" class="text-xs text-n-slate-11">
              E mais {{ participants.length - 6 }} participantes
            </p>
          </div>
        </section>
        <section
          class="rounded-xl border border-n-weak p-4 text-xs leading-5 text-n-slate-11"
        >
          <h2 class="mb-2 text-sm font-semibold text-n-slate-12">
            Converse e compartilhe
          </h2>
          <p>
            Inclua exemplos e passos claros. Uma resposta útil pode ajudar toda
            a comunidade.
          </p>
          <p class="mt-2">
            Encontrou a resposta? O autor pode marcá-la como solução para
            facilitar a consulta.
          </p>
        </section>
      </aside>
    </div>

    <Teleport to="body">
      <div
        v-if="reportTarget"
        class="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
        @click.self="reportTarget = null"
      >
        <form
          class="w-full max-w-md rounded-2xl border border-n-weak bg-n-solid-1 p-6 shadow-2xl"
          @submit.prevent="submitReport"
        >
          <div class="flex items-start gap-3">
            <span
              class="grid size-10 place-items-center rounded-lg bg-n-amber-3 text-n-amber-10"
            >
              <Icon icon="i-lucide-flag" class="size-5" />
            </span>
            <div>
              <h2 class="text-base font-semibold text-n-slate-12">
                Denunciar conteúdo
              </h2>
              <p class="mt-1 text-sm text-n-slate-10">
                A equipe de moderação analisará a denúncia. O autor não verá sua
                identidade.
              </p>
            </div>
          </div>
          <div class="mt-5 space-y-4">
            <div>
              <label class="mb-1 block text-sm font-medium text-n-slate-12"
                >Motivo</label
              ><Select
                v-model="reportForm.reason"
                :options="reportReasons"
                class="w-full [&>select]:w-full"
              />
            </div>
            <TextArea
              v-model="reportForm.details"
              label="Detalhes adicionais"
              placeholder="Explique o problema para ajudar na análise."
              :max-length="1000"
              show-character-count
              min-height="6rem"
            />
          </div>
          <div class="mt-5 flex justify-end gap-3">
            <Button
              type="button"
              label="Cancelar"
              color="slate"
              variant="outline"
              @click="reportTarget = null"
            /><Button
              type="submit"
              label="Enviar denúncia"
              color="amber"
              :is-loading="interactionId === 'report'"
              :disabled="interactionId === 'report'"
            />
          </div>
        </form>
      </div>
    </Teleport>
  </main>
</template>
